import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { extractAdditionalInfoIds } from '@modules/rx-intuitive-crown-additional-info/utils/extract-additional-info-ids';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { TranslateService } from '@ngx-translate/core';
import { ImplantsViewService } from '@shared/implants-view/implants-view.service';
import { combineLatest, defer, EMPTY, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { ImplantPositionModalService } from '../components/rx-intuitive-implant-position-modal/implant-position-modal.service';
import type {
	RxIntuitiveImplantsViewModalOpenParams,
	RxIntuitiveImplantsViewModalResult,
	ToothForImplantsViewModal
} from '../components/rx-intuitive-implants-view-modal/rx-intuitive-implants-view-modal.model';
import { RxIntuitiveImplantsViewModalService } from '../components/rx-intuitive-implants-view-modal/rx-intuitive-implants-view-modal.service';
import { getToothNumberById, IntuitiveToothForDiagram, TeethNumberingSystem } from '../models';
import { IntuitiveIndication, toUnitTypeOrDefault } from '../models/intuitive-indication';
import { ToothModalOpenOptions, ToothModalResult, ToothModalService, TOOTH_MODAL_ACTION } from '../models/tooth-modal';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { RxIntuitiveTeethHelperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-teeth-helper.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { OrderQuery } from '@modules/order/state/order-query';
import { RxIntuitiveTeethDiagramQuery } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-query';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';

/**
 * Indications that require modal dialogs instead of side tooth editor (non-bridge mode).
 */
const INDICATIONS_WITH_MODALS: IntuitiveIndication[] = [IntuitiveIndication.ImplantPosition, IntuitiveIndication.ImplantBased];

/**
 * Indications that require modal dialogs in bridge mode.
 */
const BRIDGE_INDICATIONS_WITH_MODALS: IntuitiveIndication[] = [IntuitiveIndication.BridgeImplantBased];

/**
 * Determines if an indication uses a modal dialog flow.
 */
export const isIndicationWithModals = (indication: IntuitiveIndication, areModalsSuppressed: boolean): boolean => {
	return !areModalsSuppressed && INDICATIONS_WITH_MODALS.includes(indication);
};

/**
 * Determines if a bridge indication uses a modal dialog flow.
 */
export const isBridgeIndicationWithModals = (indication: IntuitiveIndication, areModalsSuppressed: boolean): boolean => {
	return !areModalsSuppressed && BRIDGE_INDICATIONS_WITH_MODALS.includes(indication);
};

/**
 * Callback interface for handling modal results.
 * Facades implement this to apply store updates after modal closes.
 */
export interface ModalResultHandler {
	onSave(toothIds: number[], partialUpdate: Partial<Tooth>, indicationId: IntuitiveIndication): void;
	onDelete(toothIds: number[]): void;
	onClearedFields?(toothIds: number[], clearedFields: readonly string[]): void;
}

@Injectable()
export class RxIntuitiveToothEditorRoter {
	constructor(
		private readonly teethHelper: RxIntuitiveTeethHelperService,
		private readonly implantPositionModalService: ImplantPositionModalService,
		private readonly implantsViewModalService: RxIntuitiveImplantsViewModalService,
		private readonly implantsViewService: ImplantsViewService,
		private readonly translate: TranslateService,
		private readonly logger: LoggerService,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService,
		private readonly shellQuery: ShellQuery,
		private readonly hostPlatformService: HostPlatformService,
		private readonly orderQuery: OrderQuery,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery
	) {}

	/**
	 * Opens appropriate tooth modal based on indication type.
	 * Returns null if no modal is needed for this indication.
	 *
	 * @param indication - The indication to open modal for
	 * @param handler - Callback handler for modal results
	 * @returns Observable that emits modal result, or null if no modal needed
	 */
	openModalForIndicationIfNeeded(indication: IntuitiveIndication, handler: ModalResultHandler): Observable<ToothModalResult> | null {
		if (!isIndicationWithModals(indication, this.teethHelper.areImplantModalsSuppressed)) {
			return null;
		}

		return this.openModalForIndicationInternal(indication, handler);
	}

	/**
	 * Opens appropriate bridge tooth modal based on indication type.
	 * Returns null if no modal is needed for this indication.
	 *
	 * @param indication - The indication to open modal for
	 * @param handler - Callback handler for modal results
	 * @returns Observable that emits modal result, or null if no modal needed
	 */
	openBridgeModalForIndicationIfNeeded(
		indication: IntuitiveIndication,
		handler: ModalResultHandler
	): Observable<ToothModalResult> | null {
		if (!isBridgeIndicationWithModals(indication, this.teethHelper.areImplantModalsSuppressed)) {
			return null;
		}

		return this.openBridgeModalForIndicationInternal(indication, handler);
	}

	/**
	 * Converts indication to unit type, using default if needed.
	 */
	toUnitType(indicationId: IntuitiveIndication): number {
		return toUnitTypeOrDefault(indicationId, this.indicationMapper.getDefaultUnitType());
	}

	/**
	 * Builds parameters for opening the implants view modal.
	 *
	 * @param teethSource - The source of teeth (teethFlat for regular mode, selectedBridgeTeeth for bridge mode)
	 * @param selectedToothIds - IDs of selected teeth
	 * @param requestedIndication - Indication the user selected to open the modal
	 */
	buildImplantsViewModalOpenParams(
		teethSource: Tooth[],
		selectedToothIds: number[],
		requestedIndication: IntuitiveIndication
	): RxIntuitiveImplantsViewModalOpenParams {
		const teethNumberingSystem = this.teethDiagramQuery.teethNumberingSystem;

		const teethForModal: ToothForImplantsViewModal[] = selectedToothIds
			.map(id => {
				const tooth = teethSource.find(t => t.ToothID === id);

				if (!tooth) {
					return null;
				}

				return {
					toothId: tooth.ToothID ?? 0,
					indicationId: requestedIndication,
					bridgeId: tooth.BridgeIndex,
					toothNumberInSystem: getToothNumberById({ toothId: tooth.ToothID ?? 0, teethNumberingSystem }),
					customLabMaterialGroup: tooth.CustomLabMaterialGroup ?? undefined,
					implantsViewData: tooth.ImplantsViewData,
					implantTypeId: tooth.ImplantTypeID,
					restorationTypeIds: {
						ImplantBasedRestorationTypeId: tooth.ImplantBasedRestorationTypeId,
						AbutmentType: tooth.AbutmentType,
						AbutmentMaterialId: tooth.AbutmentMaterialId
					},
					material: tooth.MaterialID,
					shadeSystem: tooth.ShadeSystemId,
					shadeBody: tooth.ShadeBody,
					specification: tooth.SpecificationId,
					additionalInfoIds: extractAdditionalInfoIds(tooth)
				} satisfies ToothForImplantsViewModal;
			})
			.filter((tooth): tooth is NonNullable<typeof tooth> => tooth != null);

		const clinicalNumbersJoined = selectedToothIds.map(id => getToothNumberById({ toothId: id, teethNumberingSystem })).join(', ');
		const toothLabel = this.translate.instant('ToothEditor.Tooth') as string;

		return {
			teeth: teethForModal,
			headerToothTitle: `${toothLabel} ${clinicalNumbersJoined}`,
			procedureId: this.orderQuery.procedure?.Id,
			procedureMapTypeId: this.orderQuery.procedureMap?.TypeId,
			isBridge: requestedIndication === IntuitiveIndication.BridgeImplantBased,
			shouldValidateForSend$: this.shellQuery.shouldValidateForSend$,
			isReadOnly$: combineLatest([this.shellQuery.getReadOnlyState('toothEditor'), this.hostPlatformService.isIteroLab$]).pipe(
				map(([isReadOnly, isIteroLab]) => isReadOnly || isIteroLab)
			)
		} satisfies RxIntuitiveImplantsViewModalOpenParams;
	}

	/**
	 * Generic handler for opening tooth modals (e.g., ImplantPositionModal).
	 *
	 * @param modalService - Service implementing ToothModalService interface
	 * @param selectedToothIds - IDs of selected teeth
	 * @param initialTooth - Initial tooth data (typically from first selected tooth)
	 * @param indicationId - The indication being assigned
	 * @param handler - Callback handler for modal results
	 * @param options - Optional modal-specific context
	 */
	private openToothModal(
		modalService: ToothModalService,
		selectedToothIds: number[],
		initialTooth: Tooth,
		indicationId: IntuitiveIndication,
		handler: ModalResultHandler,
		options?: ToothModalOpenOptions
	): Observable<ToothModalResult> {
		const teethNumberingSystem = this.teethDiagramQuery.teethNumberingSystem;
		const teethRecord = this.teethHelper.teethRecord;

		const teethForModal: IntuitiveToothForDiagram[] = selectedToothIds
			.map(id => this.teethHelper.getToothById(id, teethRecord))
			.filter((tooth): tooth is Tooth => tooth != null)
			.map(tooth => this.toBasicDiagramVM(tooth, teethNumberingSystem));

		return modalService.open(teethForModal, initialTooth, options).pipe(
			take(1),
			tap(result => {
				if (result.action === TOOTH_MODAL_ACTION.Save && result.payload.toothPartialUpdate != null) {
					handler.onSave(selectedToothIds, result.payload.toothPartialUpdate, indicationId);

					this.logger.info(`Indication ${indicationId}'s modal closed with changes`, {
						module: 'EditorRoutingService',
						extendedParameters: { count: String(selectedToothIds.length) }
					});
				}
			})
		);
	}

	/**
	 * Creates a basic diagram view model for modal display.
	 * Note: This is a simplified version - facades may need full toDiagramVM for bridge/validation info.
	 */
	private toBasicDiagramVM(tooth: Tooth, teethNumberingSystem: TeethNumberingSystem): IntuitiveToothForDiagram {
		const indicationId = this.indicationMapper.getUnitTypeIndicationForDiagram(tooth.UnitTypeID);
		const toothNumberInSystem = getToothNumberById({
			toothId: tooth.ToothID ?? 0,
			teethNumberingSystem
		});

		return {
			toothId: tooth.ToothID ?? 0,
			toothNumberInSystem: toothNumberInSystem ?? 0,
			indicationId,
			svgSymbolId: '', // Not needed for modal display
			isSelected: false,
			bridgeId: tooth.BridgeIndex,
			bridge: undefined,
			isInvalid: false,
			isInvalidForSend: false
		};
	}

	/**
	 * Opens the implants view modal.
	 *
	 * @param teethSource - Source teeth array (teethFlat for standard, selectedBridgeTeeth for bridge)
	 * @param selectedToothIds - IDs of selected teeth
	 * @param indicationId - The indication being assigned
	 * @param handler - Callback handler for modal results
	 */
	private openImplantsViewModal(
		teethSource: Tooth[],
		selectedToothIds: number[],
		indicationId: IntuitiveIndication,
		handler: ModalResultHandler
	): Observable<RxIntuitiveImplantsViewModalResult> {
		const openParams = this.buildImplantsViewModalOpenParams(teethSource, selectedToothIds, indicationId);

		return this.implantsViewModalService.open(openParams).pipe(
			take(1),
			tap(result => {
				if (result.action === TOOTH_MODAL_ACTION.Save && result.payload.toothPartialUpdate != null) {
					handler.onSave(selectedToothIds, result.payload.toothPartialUpdate, indicationId);

					if (handler.onClearedFields && result.payload.pendingAdditionalInfoClears) {
						const clearedFields = result.payload.pendingAdditionalInfoClears.flatMap(entry => entry.clearedFields);

						handler.onClearedFields(selectedToothIds, clearedFields);
					}

					return;
				}

				if (result.action === TOOTH_MODAL_ACTION.Delete) {
					handler.onDelete(selectedToothIds);
				}
			})
		);
	}

	/**
	 * Internal implementation for opening modals in standard mode.
	 */
	private openModalForIndicationInternal(indication: IntuitiveIndication, handler: ModalResultHandler): Observable<ToothModalResult> {
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			return EMPTY;
		}

		const teethRecord = this.teethHelper.teethRecord;
		const initialTooth = this.teethHelper.getToothById(selectedToothIds[0], teethRecord);

		if (!initialTooth) {
			return EMPTY;
		}

		let modal$: Observable<ToothModalResult>;

		switch (indication) {
			case IntuitiveIndication.ImplantPosition: {
				modal$ = this.implantsViewService.shouldShowNewImplantsView()
					? this.openImplantsViewModal(this.teethDiagramQuery.teethFlat, selectedToothIds, indication, handler)
					: this.openToothModal(this.implantPositionModalService, selectedToothIds, initialTooth, indication, handler);
				break;
			}
			case IntuitiveIndication.ImplantBased: {
				modal$ = this.openImplantsViewModal(this.teethDiagramQuery.teethFlat, selectedToothIds, indication, handler);
				break;
			}
			default:
				this.logger.warn(`No modal configured for indication: ${indication}`, {
					module: 'EditorRoutingService'
				});

				return EMPTY;
		}

		return defer(() => modal$);
	}

	/**
	 * Internal implementation for opening modals in bridge mode.
	 */
	private openBridgeModalForIndicationInternal(
		indication: IntuitiveIndication,
		handler: ModalResultHandler
	): Observable<ToothModalResult> {
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			return EMPTY;
		}

		if (indication === IntuitiveIndication.BridgeImplantBased) {
			return this.openImplantsViewModal(
				this.intuitiveQuery.selectedBridgeTeeth,
				selectedToothIds,
				IntuitiveIndication.BridgeImplantBased,
				handler
			);
		}

		return EMPTY;
	}
}
